// src/components/Sections/Admin/Dashboard/index.jsx
//
// Inicio del panel. Antes eran 8 números sueltos del mismo tamaño y una lista de
// botones; ahora se lee de arriba abajo: cómo va el día → estado general →
// qué requiere atención → tendencia y talleres que más venden → accesos.
import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Spin, Tooltip as AntTooltip } from 'antd';
import {
  FiRefreshCw, FiUsers, FiClock, FiGrid, FiAlertTriangle, FiAlertCircle, FiCalendar,
  FiCreditCard, FiFileText, FiArrowRight, FiShoppingBag, FiCheckCircle, FiBarChart2,
  FiUserPlus, FiAward, FiTrendingUp, FiMonitor, FiMapPin,
} from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend,
} from 'chart.js';
import Dashboard from '../../../../service/Dashboard';
import Reporteria from '../../../../service/Reporteria';
import useAuthContext from '../../../../hooks/useAuthContext';
import { can, requiereAprobacion } from '../../../../config/rbac';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);
dayjs.locale('es');

const DIAS_GRAFICO = 14;
const DIAS_TOP = 30;

const numero = (n) => (n ?? 0).toLocaleString('es-CL');
const clp = (n) => `$${Math.round(n || 0).toLocaleString('es-CL')}`;
const clpCorto = (n) => {
  const v = n || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toLocaleString('es-CL', { maximumFractionDigits: 1 })}M`;
  if (v >= 1_000) return `$${Math.round(v / 1_000).toLocaleString('es-CL')}K`;
  return `$${v}`;
};

/* ── Piezas ─────────────────────────────────────────────────────────────── */

const Tarjeta = ({ className = '', children }) => (
  <section className={`rounded-3xl bg-white border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,.04),0_8px_24px_-14px_rgba(0,0,0,.10)] ${className}`}>
    {children}
  </section>
);

const TituloTarjeta = ({ icon, children, extra }) => (
  <header className="flex items-center justify-between gap-3 px-6 pt-5 pb-3">
    <h2 className="flex items-center gap-2.5 text-base font-extrabold text-gray-900 m-0">
      <span className="w-8 h-8 rounded-lg bg-blue-50 text-primary flex items-center justify-center">{icon}</span>
      {children}
    </h2>
    {extra}
  </header>
);

const Kpi = ({ icon, label, value, detalle, to, tono = 'azul' }) => {
  const tonos = {
    azul: 'bg-blue-50 text-primary',
    amarillo: 'bg-secondary/30 text-[#5c4300]',
    rojo: 'bg-red-50 text-red-600',
  };
  const cuerpo = (
    <>
      <span className={`w-11 h-11 flex-shrink-0 rounded-xl flex items-center justify-center ${tonos[tono]}`}>{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-gray-500 m-0">{label}</p>
        <p className="text-2xl font-extrabold text-gray-900 leading-tight m-0 tabular-nums">{value}</p>
        {detalle && <p className="text-xs text-gray-500 m-0 mt-0.5 truncate">{detalle}</p>}
      </div>
      {to && <FiArrowRight className="text-gray-300 group-hover:text-primary transition-colors flex-shrink-0" />}
    </>
  );
  const cls = 'group flex items-center gap-3 p-4 rounded-2xl bg-white border border-gray-100 transition-colors';
  return to
    ? <Link to={to} className={`${cls} hover:border-secondary hover:bg-[#fffbea]`}>{cuerpo}</Link>
    : <div className={cls}>{cuerpo}</div>;
};

const SEVERIDAD = {
  alta:  { chip: 'bg-red-50 text-red-700',            icono: 'bg-red-50 text-red-600',            label: 'Urgente' },
  media: { chip: 'bg-secondary/40 text-[#5c4300]',    icono: 'bg-secondary/30 text-[#5c4300]',    label: 'Pronto' },
  info:  { chip: 'bg-blue-50 text-primary',           icono: 'bg-blue-50 text-primary',           label: 'Revisar' },
};

const AlertaItem = ({ icon, valor, texto, severidad, to, accion }) => {
  const s = SEVERIDAD[severidad];
  return (
    <li>
      <Link to={to} className="group flex items-center gap-3 p-3 rounded-2xl hover:bg-gray-50 transition-colors">
        <span className={`w-10 h-10 flex-shrink-0 rounded-xl flex items-center justify-center ${s.icono}`}>{icon}</span>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-gray-800 m-0 leading-snug">
            <strong className="font-extrabold text-gray-900 tabular-nums">{numero(valor)}</strong> {texto}
          </p>
          <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${s.chip}`}>{s.label}</span>
        </div>
        <span className="hidden sm:inline-flex items-center gap-1 text-xs font-bold text-primary opacity-70 group-hover:opacity-100 transition-opacity">
          {accion} <FiArrowRight size={13} />
        </span>
      </Link>
    </li>
  );
};

/* ── Pantalla ───────────────────────────────────────────────────────────── */

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const verReportes = can(user, 'reporteria');
  // Igual que RequireAprobador en App.js: la cola la resuelve quien edita sin aprobación.
  const apruebaCambios = can(user, 'talleres', 'editar') && !requiereAprobacion(user, 'talleres');

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  // Tendencia y top talleres vienen de reportería: si el rol no la tiene, o la
  // consulta falla, esas tarjetas no se muestran y el resto del inicio sigue igual.
  const [serie, setSerie] = useState(null);
  const [top, setTop] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    const hoy = dayjs();
    const reportes = verReportes ? Promise.allSettled([
      Reporteria.GetIngresosPorPeriodo({
        fechaInicio: hoy.subtract(DIAS_GRAFICO - 1, 'day').format('YYYY-MM-DD'),
        fechaFin: hoy.format('YYYY-MM-DD'),
        agruparPor: 'dia',
      }),
      Reporteria.GetIngresosPorTaller({
        fechaInicio: hoy.subtract(DIAS_TOP - 1, 'day').format('YYYY-MM-DD'),
        fechaFin: hoy.format('YYYY-MM-DD'),
        limite: 5,
      }),
    ]) : Promise.resolve([]);

    try {
      const [kpis, [periodo, talleres] = []] = await Promise.all([Dashboard.getKpis(), reportes]);
      setData(kpis);
      setLastUpdate(new Date());

      if (periodo?.status === 'fulfilled') {
        // La API solo trae los días con pagos: se rellenan los vacíos con 0 para
        // que el gráfico muestre los 14 días seguidos.
        const porDia = Object.fromEntries(
          (periodo.value?.desglosePorPeriodo || []).map(d => [dayjs(d._id).format('YYYY-MM-DD'), d.totalIngresos || 0])
        );
        setSerie(Array.from({ length: DIAS_GRAFICO }, (_, i) => {
          const d = hoy.subtract(DIAS_GRAFICO - 1 - i, 'day');
          return { fecha: d, total: porDia[d.format('YYYY-MM-DD')] || 0 };
        }));
      } else setSerie(null);

      setTop(talleres?.status === 'fulfilled' ? (talleres.value?.topTalleres || []).slice(0, 5) : null);
    } catch (e) {
      setError('No se pudieron cargar los indicadores. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, [verReportes]);

  useEffect(() => { cargar(); }, [cargar]);

  const v = data?.ventas;
  const presencialPct = v && v.ingresoHoy > 0 ? Math.round(((v.ingresoPresencialHoy || 0) / v.ingresoHoy) * 100) : 0;

  // Ordenadas por severidad: lo urgente arriba.
  const alertas = data ? [
    data.horarios.capacidadCritica > 0 && {
      key: 'capacidad', severidad: 'alta', icon: <FiAlertTriangle size={18} />,
      valor: data.horarios.capacidadCritica, texto: 'horarios con menos del 20% de cupos',
      to: '/admin/talleres/lista', accion: 'Ver horarios',
    },
    data.talleres.sinInscritos > 0 && {
      key: 'sin-inscritos', severidad: 'alta', icon: <FiAlertCircle size={18} />,
      valor: data.talleres.sinInscritos, texto: 'talleres activos sin inscritos (riesgo de cancelación)',
      to: '/admin/talleres/lista', accion: 'Ver talleres',
    },
    data.inscripciones.pendientesPago > 0 && {
      key: 'pendientes', severidad: 'media', icon: <FiClock size={18} />,
      valor: data.inscripciones.pendientesPago, texto: 'inscripciones con pago sin confirmar',
      to: '/admin/talleres/lista', accion: 'Ver talleres',
    },
    data.inscripciones.proximasVencer > 0 && {
      key: 'vencer-inscripciones', severidad: 'media', icon: <FiCalendar size={18} />,
      valor: data.inscripciones.proximasVencer, texto: 'suscripciones vencen en los próximos 30 días',
      to: '/admin/talleres/lista', accion: 'Ver talleres',
    },
    data.matriculas.proximasVencer > 0 && {
      key: 'vencer-matriculas', severidad: 'media', icon: <FiCreditCard size={18} />,
      valor: data.matriculas.proximasVencer, texto: 'matrículas vencen en los próximos 30 días',
      to: '/admin/matriculas/usuarios', accion: 'Ver matrículas',
    },
    apruebaCambios && data.revision.cambiosPendientes > 0 && {
      key: 'cambios', severidad: 'info', icon: <FiFileText size={18} />,
      valor: data.revision.cambiosPendientes, texto: 'cambios de taller esperan revisión',
      to: '/admin/talleres/cambios-revisiones', accion: 'Revisar',
    },
  ].filter(Boolean) : [];

  // Solo los accesos que este usuario puede abrir (las mismas reglas que las rutas).
  const accesos = [
    { label: 'Nueva venta', icon: <FiShoppingBag size={18} />, to: '/admin/ventas/nueva', ok: can(user, 'ventas', 'editar'), destacado: true },
    { label: 'Talleres', icon: <FiGrid size={18} />, to: '/admin/talleres/lista', ok: can(user, 'talleres') },
    { label: 'Crear taller', icon: <FiTrendingUp size={18} />, to: '/admin/talleres/crear', ok: can(user, 'talleres', 'editar') },
    { label: 'Usuarios', icon: <FiUserPlus size={18} />, to: '/admin/usuarios', ok: can(user, 'usuarios') },
    { label: 'Matrículas', icon: <FiAward size={18} />, to: '/admin/matriculas/usuarios', ok: can(user, 'matriculas') },
    { label: 'Recintos', icon: <FiMapPin size={18} />, to: '/admin/recintos', ok: can(user, 'recintos') },
    { label: 'Reportería', icon: <FiBarChart2 size={18} />, to: '/admin/reporteria/dashboard', ok: verReportes },
    { label: 'Cambios', icon: <FiFileText size={18} />, to: '/admin/talleres/cambios-revisiones', ok: apruebaCambios, badge: data?.revision?.cambiosPendientes },
  ].filter(a => a.ok);

  const nombre = (user?.name || '').split(' ')[0];
  const maxSerie = serie ? Math.max(...serie.map(s => s.total)) : 0;
  const totalSerie = serie ? serie.reduce((a, s) => a + s.total, 0) : 0;

  return (
    <div className="px-4 sm:px-6 py-6 max-w-[1280px]">
      {/* ── Encabezado ───────────────────────────────────────────────────── */}
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-sm font-semibold text-gray-500 capitalize m-0">{dayjs().format('dddd D [de] MMMM')}</p>
          <h1 className="text-3xl font-extrabold text-gray-900 leading-tight m-0">
            Hola{nombre && <>, <span className="hl-amarillo">{nombre}</span></>}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdate && (
            <span className="text-xs text-gray-500 mr-1">
              Actualizado {lastUpdate.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button type="button" onClick={cargar} disabled={loading} aria-label="Actualizar indicadores"
            className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:border-primary hover:text-primary transition-colors disabled:opacity-60">
            <FiRefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          {can(user, 'ventas', 'editar') && (
            <button type="button" onClick={() => navigate('/admin/ventas/nueva')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white text-sm font-bold shadow-sm hover:brightness-110 transition-all">
              <FiShoppingBag size={15} /> Nueva venta
            </button>
          )}
        </div>
      </header>

      {error && <Alert message={error} type="error" showIcon className="mb-4" />}

      <Spin spinning={loading && !!data}>
        {/* ── Hoy ────────────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden rounded-3xl bg-[#00266b] text-white p-6 sm:p-7 mb-6">
          <div aria-hidden className="absolute inset-0 opacity-[.08] pointer-events-none" style={{
            backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '22px 22px',
          }} />
          <div className="relative grid grid-cols-1 lg:grid-cols-[1.4fr_1fr_1fr] gap-6 lg:gap-0 lg:divide-x lg:divide-white/10">
            <div className="lg:pr-8">
              <p className="text-xs font-bold uppercase tracking-widest text-secondary m-0">Ingresos de hoy</p>
              <p className="text-4xl sm:text-5xl font-extrabold leading-tight m-0 mt-1 tabular-nums">
                {data ? clp(v.ingresoHoy) : '—'}
              </p>
              {data && v.ingresoHoy > 0 && (
                <div className="mt-4 max-w-md">
                  <div className="flex h-2 rounded-full overflow-hidden bg-white/10" aria-hidden>
                    <span className="bg-secondary" style={{ width: `${100 - presencialPct}%` }} />
                    <span className="bg-[#6fa0ff]" style={{ width: `${presencialPct}%` }} />
                  </div>
                  <div className="flex justify-between gap-4 mt-2 text-xs text-blue-100">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-secondary" /> Online {clp(v.ingresoOnlineHoy)}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-[#6fa0ff]" /> Presencial {clp(v.ingresoPresencialHoy)}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="lg:px-8">
              <p className="text-xs font-bold uppercase tracking-widest text-blue-200 m-0">Órdenes de hoy</p>
              <p className="text-3xl font-extrabold m-0 mt-1 tabular-nums">{data ? numero(v.ordenesHoy + v.ventasPresencialesHoy) : '—'}</p>
              {data && (
                <p className="text-sm text-blue-100 m-0 mt-1">
                  <FiMonitor className="inline -mt-0.5 mr-1" size={13} />{numero(v.ordenesHoy)} online · {numero(v.ventasPresencialesHoy)} presenciales
                </p>
              )}
            </div>
            <div className="lg:pl-8">
              <p className="text-xs font-bold uppercase tracking-widest text-blue-200 m-0">Esta semana</p>
              <p className="text-3xl font-extrabold m-0 mt-1 tabular-nums">{data ? clpCorto(v.ingresoSemana) : '—'}</p>
              {data && (
                <AntTooltip title={clp(v.ingresoSemana)}>
                  <p className="text-sm text-blue-100 m-0 mt-1 cursor-help">Recaudado esta semana</p>
                </AntTooltip>
              )}
            </div>
          </div>
        </section>

        {/* ── Estado general ─────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-6">
          <Kpi icon={<FiUsers size={20} />} label="Inscritos activos" value={data ? numero(data.inscripciones.activas) : '—'}
            detalle="Inscripciones confirmadas y vigentes" />
          <Kpi icon={<FiGrid size={20} />} label="Talleres activos" value={data ? numero(data.talleres.activos) : '—'}
            detalle={data && data.talleres.sinInscritos > 0 ? `${numero(data.talleres.sinInscritos)} sin inscritos` : 'Todos con inscritos'}
            to={can(user, 'talleres') ? '/admin/talleres/lista' : undefined} />
          <Kpi icon={<FiClock size={20} />} label="Pendientes de pago" value={data ? numero(data.inscripciones.pendientesPago) : '—'}
            detalle="Sin confirmación de pago" tono={data?.inscripciones.pendientesPago > 0 ? 'amarillo' : 'azul'} />
          <Kpi icon={<FiFileText size={20} />} label="Cambios por revisar" value={data ? numero(data.revision.cambiosPendientes) : '—'}
            detalle="Ediciones de talleres en cola" tono={data?.revision.cambiosPendientes > 0 ? 'amarillo' : 'azul'}
            to={apruebaCambios ? '/admin/talleres/cambios-revisiones' : undefined} />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1.35fr_1fr] gap-6 mb-6">
          {/* ── Requiere atención ─────────────────────────────────────────── */}
          <Tarjeta>
            <TituloTarjeta icon={<FiAlertTriangle size={16} />}
              extra={alertas.length > 0 && (
                <span className="px-2.5 py-1 rounded-full bg-red-50 text-red-700 text-xs font-bold">{alertas.length} pendientes</span>
              )}>
              Requiere <span className="hl-amarillo">atención</span>
            </TituloTarjeta>
            <div className="px-3 pb-4">
              {!data ? (
                <div className="py-10 text-center"><Spin /></div>
              ) : alertas.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-10 text-center">
                  <span className="w-12 h-12 rounded-full bg-blue-50 text-primary flex items-center justify-center"><FiCheckCircle size={22} /></span>
                  <p className="font-bold text-gray-900 m-0">Todo al día</p>
                  <p className="text-sm text-gray-500 m-0">No hay alertas operacionales ahora.</p>
                </div>
              ) : (
                <ul className="m-0 p-0 list-none">
                  {alertas.map(a => <AlertaItem key={a.key} {...a} />)}
                </ul>
              )}
            </div>
          </Tarjeta>

          {/* ── Accesos ───────────────────────────────────────────────────── */}
          <Tarjeta>
            <TituloTarjeta icon={<FiGrid size={16} />}>Accesos rápidos</TituloTarjeta>
            <div className="grid grid-cols-2 gap-2 px-4 pb-5">
              {accesos.map(a => (
                <Link key={a.to} to={a.to}
                  className={`group relative flex items-center gap-3 p-3 rounded-2xl border transition-colors
                    ${a.destacado
                      ? 'bg-primary border-primary !text-white hover:bg-[#00266b]'
                      : 'bg-white border-gray-100 hover:border-secondary hover:bg-[#fffbea]'}`}>
                  <span className={`w-9 h-9 flex-shrink-0 rounded-xl flex items-center justify-center transition-colors
                    ${a.destacado ? 'bg-white/15 text-white' : 'bg-blue-50 text-primary group-hover:bg-secondary group-hover:text-[#00266b]'}`}>
                    {a.icon}
                  </span>
                  <span className={`text-sm font-bold truncate ${a.destacado ? 'text-white' : 'text-gray-800'}`}>{a.label}</span>
                  {a.badge > 0 && (
                    <span className="ml-auto min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">
                      {a.badge}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </Tarjeta>
        </div>

        {/* ── Tendencia + top talleres (solo con permiso de reportería) ─────── */}
        {verReportes && (serie || top) && (
          <div className="grid grid-cols-1 xl:grid-cols-[1.35fr_1fr] gap-6">
            {serie && (
              <Tarjeta>
                <TituloTarjeta icon={<FiBarChart2 size={16} />}
                  extra={<span className="text-xs text-gray-500">{clp(totalSerie)} en {DIAS_GRAFICO} días</span>}>
                  Ingresos <span className="hl-amarillo">diarios</span>
                </TituloTarjeta>
                <div className="px-6 pb-6 h-[260px]">
                  {maxSerie === 0 ? (
                    <div className="h-full flex items-center justify-center text-sm text-gray-500">Sin pagos en los últimos {DIAS_GRAFICO} días.</div>
                  ) : (
                    <Bar
                      data={{
                        labels: serie.map(s => s.fecha.format('dd D')),
                        datasets: [{
                          label: 'Ingresos',
                          data: serie.map(s => s.total),
                          // Hoy en amarillo; el resto en azul de marca.
                          backgroundColor: serie.map((_, i) => i === serie.length - 1 ? '#feda2c' : '#0047bb'),
                          hoverBackgroundColor: serie.map((_, i) => i === serie.length - 1 ? '#e5c014' : '#00266b'),
                          borderRadius: 6,
                          maxBarThickness: 28,
                        }],
                      }}
                      options={{
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: false },
                          tooltip: {
                            callbacks: {
                              title: (items) => serie[items[0].dataIndex].fecha.format('dddd D [de] MMMM'),
                              label: (item) => ` ${clp(item.raw)}`,
                            },
                          },
                        },
                        scales: {
                          x: { grid: { display: false }, ticks: { color: '#6b7280', font: { size: 11 } } },
                          y: {
                            beginAtZero: true, border: { display: false },
                            grid: { color: '#f1f5f9' },
                            ticks: { color: '#6b7280', font: { size: 11 }, callback: (val) => clpCorto(val), maxTicksLimit: 5 },
                          },
                        },
                      }}
                    />
                  )}
                </div>
              </Tarjeta>
            )}

            {top && (
              <Tarjeta>
                <TituloTarjeta icon={<FiTrendingUp size={16} />}
                  extra={<span className="text-xs text-gray-500">Últimos {DIAS_TOP} días</span>}>
                  Talleres que más venden
                </TituloTarjeta>
                <div className="px-6 pb-6">
                  {top.length === 0 ? (
                    <p className="py-8 text-center text-sm text-gray-500 m-0">Sin ventas en el período.</p>
                  ) : (
                    <ol className="m-0 p-0 list-none space-y-3">
                      {top.map((t, i) => {
                        const pct = top[0].totalIngresos > 0 ? (t.totalIngresos / top[0].totalIngresos) * 100 : 0;
                        return (
                          <li key={t._id || t.tallerNombre}>
                            <div className="flex items-baseline justify-between gap-3">
                              <span className="flex items-baseline gap-2 min-w-0">
                                <span className={`w-6 h-6 flex-shrink-0 rounded-full text-xs font-extrabold flex items-center justify-center
                                  ${i === 0 ? 'bg-secondary text-[#00266b]' : 'bg-gray-100 text-gray-600'}`}>{i + 1}</span>
                                <span className="min-w-0">
                                  <span className="block text-sm font-bold text-gray-900 truncate" title={t.tallerNombre}>{t.tallerNombre}</span>
                                  {t.recintoNombre && <span className="block text-xs text-gray-500 truncate">{t.recintoNombre}</span>}
                                </span>
                              </span>
                              <span className="text-right flex-shrink-0">
                                <span className="block text-sm font-extrabold text-gray-900 tabular-nums">{clp(t.totalIngresos)}</span>
                                <span className="block text-xs text-gray-500">{numero(t.totalInscripciones)} inscr.</span>
                              </span>
                            </div>
                            <div className="h-1.5 mt-2 ml-8 rounded-full bg-gray-100 overflow-hidden" aria-hidden>
                              <div className={`h-full rounded-full ${i === 0 ? 'bg-secondary' : 'bg-primary'}`} style={{ width: `${pct}%` }} />
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                  <Link to="/admin/reporteria/financiero/ingresos"
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:gap-2.5 transition-all">
                    Ver reporte de ingresos <FiArrowRight size={14} />
                  </Link>
                </div>
              </Tarjeta>
            )}
          </div>
        )}
      </Spin>
    </div>
  );
};

export default AdminDashboard;
